import React from 'react';
import vars from '@variables';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import {
  CallToAction,
  Content,
  Title,
  Wrapper,
  RichTextWrapper,
  ButtonContainer,
} from './aligned-images-block.styles';
import {
  AlignedImageBlockProps,
  ALIGN_IMAGE,
} from './aligned-image-block.types';
import RichText from '@components/rich-text/rich-text';
import {
  YouTubeEmbedAtom,
  VimeoEmbedAtom,
} from '@atoms/media-atoms/media-atoms';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';

const youtubeBaseUrl = 'https://www.youtube.com/watch?v=';
const youtubeShortBaseUrl = 'https://youtu.be/';
const vimeoBaseUrl = 'https://vimeo.com/';

const isUrlValid = (url: string) => {
  return (
    url.startsWith(youtubeBaseUrl) ||
    url.startsWith(vimeoBaseUrl) ||
    url.startsWith(youtubeShortBaseUrl)
  );
};

const ExternalMedia = ({ mediaUrl }: { mediaUrl: string }) => {
  if (
    mediaUrl.startsWith(youtubeBaseUrl) ||
    mediaUrl.startsWith(youtubeShortBaseUrl)
  ) {
    return <YouTubeEmbedAtom src={mediaUrl} className={'gatsby-image-pos'} />;
  }
  if (mediaUrl.startsWith(vimeoBaseUrl)) {
    return <VimeoEmbedAtom src={mediaUrl} className={'gatsby-image-pos'} />;
  }
  return null;
};

const ImageContent = ({
  gatsbyBackgroundImage,
  format,
  imageSrc,
  imageAlt,
}: {
  gatsbyBackgroundImage: any;
  format: string;
  imageSrc: string;
  imageAlt: string;
}) => {
  if (
    gatsbyBackgroundImage &&
    format !== 'image/gif' &&
    format !== 'image/svg+xml'
  ) {
    return (
      <GatsbyImage
        image={gatsbyBackgroundImage}
        alt={imageAlt}
        className="gatsby-image-pos"
      />
    );
  }
  return (
    <div className="gatsby-image-pos">
      <img src={imageSrc} alt={imageAlt} />
    </div>
  );
};

const MediaOrImage = ({
  showMedia,
  mediaUrl,
  gatsbyBackgroundImage,
  format,
  imageSrc,
  imageAlt,
  imageLink,
  internalImageLink,
  imageOpenInNewTab,
}: {
  showMedia: boolean;
  mediaUrl?: string;
  gatsbyBackgroundImage: any;
  format: string;
  imageSrc: string;
  imageAlt: string;
  imageLink?: string;
  internalImageLink?: string;
  imageOpenInNewTab?: boolean;
}) => {
  const content =
    showMedia && mediaUrl ? (
      <ExternalMedia mediaUrl={mediaUrl} />
    ) : (
      <ImageContent
        gatsbyBackgroundImage={gatsbyBackgroundImage}
        format={format}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
      />
    );

  if (imageLink || internalImageLink) {
    return (
      <FlexibleLinkAtom
        url={imageLink || internalImageLink}
        className="gatsby-image-pos"
        openInNewTab={imageOpenInNewTab}
      >
        {content}
      </FlexibleLinkAtom>
    );
  }

  return content;
};

const AlignedImageBlock: React.FC<AlignedImageBlockProps> = ({
  title = '',
  anchorId,
  content,
  imageSrc,
  imageAlt = '',
  gatsbyImageData,
  format = vars.defaultImageFormat,
  alignImage = ALIGN_IMAGE.RIGHT,
  imageLink,
  imageOpenInNewTab,
  internalImageLink,
  mediaUrl,
  firstButtonLink,
  firstButtonText,
  secondButtonLink,
  secondButtonText,
}) => {
  const gatsbyBackgroundImage = getImage(gatsbyImageData);
  const showMedia = !!mediaUrl && isUrlValid(mediaUrl);
  const showCTAButtons = !!firstButtonText || !!secondButtonText;

  if (!imageSrc) return null;

  return (
    <Wrapper
      data-testid="aligned-image-block"
      alignImage={alignImage}
      id={anchorId}
    >
      <MediaOrImage
        showMedia={showMedia}
        mediaUrl={mediaUrl}
        gatsbyBackgroundImage={gatsbyBackgroundImage}
        format={format}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        imageLink={imageLink}
        internalImageLink={internalImageLink}
        imageOpenInNewTab={imageOpenInNewTab}
      />
      <Content>
        {title && <Title>{title}</Title>}
        {content?.raw && (
          <RichTextWrapper>
            <RichText content={content} />
          </RichTextWrapper>
        )}
        {showCTAButtons && (
          <ButtonContainer>
            {firstButtonText && firstButtonLink && (
              <FlexibleLinkAtom url={firstButtonLink}>
                <CallToAction>{firstButtonText}</CallToAction>
              </FlexibleLinkAtom>
            )}
            {secondButtonText && secondButtonLink && (
              <FlexibleLinkAtom url={secondButtonLink}>
                <CallToAction isSecondary>{secondButtonText}</CallToAction>
              </FlexibleLinkAtom>
            )}
          </ButtonContainer>
        )}
      </Content>
    </Wrapper>
  );
};

export default AlignedImageBlock;
